import React, { useState, useMemo } from 'react';
import { AppShell } from '../../components/layout/AppShell';
import { PageLayout } from '../../components/common/PageLayout';
import { EmptyState } from '../../components/common/EmptyState';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { PAGE_COLORS } from '../../utils/pageColors';
import { 
  useGetDeliveryJobsQuery, 
  useUpdateOrderLoadedMutation,
  useGetUserProfilesQuery 
} from '../../store/slices/apiSlice';
import { useToast } from '../../hooks/useToast';
import { Search, Filter, Calendar, Truck, User } from 'lucide-react';
import type { UserProfile } from '../../utils/types';

export const DeliveryPageComponent: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0] // Today's date as default
  );
  const [selectedDeliveryUser, setSelectedDeliveryUser] = useState('all');
  
  const [updateOrderLoaded] = useUpdateOrderLoadedMutation();
  const toast = useToast();

  // Fetch delivery users
  const { data: userProfiles, isLoading: isLoadingUsers } = useGetUserProfilesQuery({ 
    page: 1, 
    page_size: 100, 
    ordering: 'user__username' 
  });

  // Filter to show only users with delivery role
  const deliveryUsers = useMemo(() => {
    const list = userProfiles?.results || [];
    const filteredList = list.filter((p: UserProfile) => {
      const roleName = p.role_details?.name?.toLowerCase() || '';
      return roleName.includes('delivery');
    });
    
    return filteredList.map((p: UserProfile) => ({
      id: String(p.user),
      label: p.user_details.username,
      role: p.role_details?.name || 'No Role',
    }));
  }, [userProfiles]);

  // Conditional data fetching - only when both user and date are selected
  const shouldFetch = selectedDeliveryUser !== 'all' && !!selectedDate;
  
  const filters = useMemo(() => ({
    potential_delivery_date__date: selectedDate, // Use __date lookup for DateTime field
    delivery_user: selectedDeliveryUser !== 'all' ? selectedDeliveryUser : undefined,
    search: searchTerm || undefined,
    ordering: 'potential_delivery_date,-created_at',
  }), [selectedDate, selectedDeliveryUser, searchTerm]);

  const { data: deliveryData, isLoading, isError, refetch } = useGetDeliveryJobsQuery(
    filters,
    { skip: !shouldFetch }
  );

  const jobs = deliveryData?.results ?? [];

  // Transform jobs to orders array
  const orders = useMemo(() => {
    console.log('Recalculating orders, jobs count:', jobs.length);
    const result = jobs.flatMap(job => {
      const sortedOrders = [...job.orders].sort((a: any, b: any) => {
        return (a.id || '').localeCompare(b.id || '');
      });
      
      return sortedOrders.map((order: any) => ({
        ...order,
        // Add job context
        job_id: job.id,
        website_job_number: job.website_job_number,
        delivery_postcode: job.delivery_postcode,
        delivery_user_username: job.delivery_user_username,
        manufacture_date: job.manufacture_date,
      }));
    });
    console.log('Total orders:', result.length);
    if (result.length > 0) {
      console.log('Sample order loaded status:', result[0].loaded);
    }
    return result;
  }, [jobs]);

  // Handle loaded checkbox change
  const handleLoadedChange = async (orderId: string, currentLoaded: boolean) => {
    try {
      console.log('Updating order:', orderId, 'from', currentLoaded, 'to', !currentLoaded);
      const result = await updateOrderLoaded({ orderId, loaded: !currentLoaded }).unwrap();
      console.log('Update successful:', result);
      
      // Manually refetch to update the UI
      console.log('Calling refetch...');
      const refetchResult = await refetch();
      console.log('Refetch result:', refetchResult);
      
      toast.success('Order Updated', 'Loaded status updated successfully');
    } catch (error: any) {
      console.error('Update failed:', error);
      toast.error('Update Failed', error?.data?.detail || 'Could not update loaded status');
    }
  };

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return '-';
    }
  };

  // Loading state
  if (isLoadingUsers) {
    return (
      <AppShell>
        <PageLayout
          title="Delivery Jobs"
          subtitle="Manage delivery jobs"
          bgColor={PAGE_COLORS.delivery.bgColor}
          textColor={PAGE_COLORS.delivery.textColor}
          borderColor={PAGE_COLORS.delivery.borderColor}
          icon={<Truck className="w-6 h-6" />}
        >
          <LoadingSpinner />
        </PageLayout>
      </AppShell>
    );
  }

  // Error state
  if (isError) {
    return (
      <AppShell>
        <PageLayout
          title="Delivery Jobs"
          subtitle="Manage delivery jobs"
          bgColor={PAGE_COLORS.delivery.bgColor}
          textColor={PAGE_COLORS.delivery.textColor}
          borderColor={PAGE_COLORS.delivery.borderColor}
          icon={<Truck className="w-6 h-6" />}
        >
          <EmptyState
            icon={<Truck className="w-12 h-12" />}
            title="Error Loading Delivery Jobs"
            description="There was an error loading the delivery jobs. Please try again."
          />
        </PageLayout>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <PageLayout
        title="Delivery Jobs"
        subtitle="Manage delivery jobs and track loaded status"
        bgColor={PAGE_COLORS.delivery.bgColor}
        textColor={PAGE_COLORS.delivery.textColor}
        borderColor={PAGE_COLORS.delivery.borderColor}
        icon={<Truck className="w-6 h-6" />}
      >
        {/* Filters */}
        <div className="mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              {/* Search */}
              <form onSubmit={handleSearchSubmit} className="flex-1 min-w-[300px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search customer, order reference, postcode..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input pl-10 w-full"
                  />
                </div>
              </form>

              {/* Filters Label */}
              <div className="hidden sm:flex items-center gap-2 text-gray-700">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium">Filters:</span>
              </div>

              {/* Date Filter */}
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="form-input w-auto"
                />
              </div>

              {/* Delivery User Filter */}
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-500" />
                <select
                  value={selectedDeliveryUser}
                  onChange={(e) => setSelectedDeliveryUser(e.target.value)}
                  className="form-input w-auto min-w-[200px]"
                >
                  <option value="all">All Delivery Users</option>
                  {deliveryUsers.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.label} • [{user.role}]
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Empty State - No filters selected */}
        {!shouldFetch && (
          <EmptyState
            icon={<Truck className="w-12 h-12" />}
            title="Select Filters"
            description="Please select a delivery user and date to view jobs"
          />
        )}

        {/* Loading State */}
        {shouldFetch && isLoading && (
          <LoadingSpinner />
        )}

        {/* Table */}
        {shouldFetch && !isLoading && (
          <>
            {orders.length === 0 ? (
              <EmptyState
                icon={<Truck className="w-12 h-12" />}
                title="No Delivery Jobs Found"
                description="No jobs match the selected filters"
              />
            ) : (
              <>
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Order #
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Postcode
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Reference
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Delivery User
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Glass Side 1
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Glass Side 2
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Glass Side 3
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Width (mm)
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Height (mm)
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Diameter (mm)
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Drawing
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Quantity
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Manufacture Date
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Rack
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Loaded
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {orders.map((order: any) => {
                          const hasDrawing = order.images?.some((img: any) => img.image_type === 'drawing') ?? false;
                          const glassSide1 = order.glass_side_one_display ?? '-';
                          const glassSide2 = order.glass_side_two_display ?? '-';
                          const glassSide3 = order.middle_glass_panel_display ?? '-';
                          
                          return (
                            <tr 
                              key={order.id}
                              className={order.loaded ? 'bg-green-50' : ''}
                            >
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {order.website_job_number || '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {order.delivery_postcode || '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {order.reference || '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {order.delivery_user_username || '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {glassSide1}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {glassSide2}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {glassSide3}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {order.glass_width || '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {order.glass_height || '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {order.diameter_mm || '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {hasDrawing ? 'Yes' : 'No'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {order.quantity || '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {formatDate(order.manufacture_date)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {order.rack || '-'}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <input
                                  type="checkbox"
                                  checked={order.loaded || false}
                                  onChange={() => handleLoadedChange(order.id, order.loaded)}
                                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded cursor-pointer"
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </PageLayout>
    </AppShell>
  );
};
